// BulletinPreview — CSS stand-ins for what the customer app draws from an app_bulletins row.
//
// Close enough to choose by, never pixel-exact: the app draws in SwiftUI with iOS fonts and its
// own symbols; this draws with CSS, the same fonts where the browser has them (a Mac does), and
// emoji for the symbols. Every screen that shows one of these says it's a preview.
//
//   <NotePaper note s/>     one window note — paper, fastener, colour, handwriting, picture,
//                           stamp, tilt. `s` scales the whole note (1 = the editor's stage).
//   <BannerCard banner/>    one Shop banner in the app's navy and gold.
//   <WindowMock notes/>     the window on Home with the live notes taped to the upper pane.
//   <ShopMock banners/>     the top of the Shop screen with the live banners.
// Every key it reads comes from bulletinDesigns.js; an unknown key draws the default.
import { useEffect, useRef, useState } from "react";
import { COLOR, FONT, ICON, LINK_LABEL, SCREENS, STYLE_LABEL, FASTENER_LABEL, CAPACITY } from "./bulletinDesigns";

export const NAVY = "#072C76";
export const GOLD = "#F4C120";

// The handwriting the app uses when the operator didn't pick one (BulletinWall.swift NotePaper.ink).
const MARKER = FONT.marker;
const DEFAULT_BODY = { paper: FONT.hand, sticky: FONT.hand, index: FONT.chalk, kraft: FONT.chalk, flyer: FONT.chalk };

// What the app reaches for when the paper is left blank — the first of its preferred papers for
// the kind. The app varies it per note; the preview shows the likeliest.
const APP_PAPER = { offer: "sticky", deal: "sticky", shop_local: "kraft", announcement: "paper", event: "index", coming_soon: "flyer" };

// Each paper's own look when no colour is chosen: fill, title ink, body ink, natural width.
const PAPER = {
  paper:      { fill: "#FBFAF6", title: "#1C3A8C", body: "#1C3A8C", w: 176 },
  sticky:     { fill: "#FBE68A", title: "#1E1E1E", body: "#2A2A2A", w: 156, square: true },
  index:      { fill: "#FFFDF8", title: "#A31E1E", body: "#262626", w: 192 },
  kraft:      { fill: "#C9A274", title: "#24170C", body: "#2B1C10", w: 182 },
  flyer:      { fill: "#FFFFFF", title: NAVY, body: "#222222", w: 170, centred: true },
  coupon:     { fill: "#F6EED8", title: "#2B2418", body: "#2B2418", w: 192 },
  ticket:     { fill: "#FBD1A6", title: "#4A2408", body: "#4A2408", w: 206 },
  polaroid:   { fill: "#FFFFFF", title: "#1E1E1E", body: "#333333", w: 160, centred: true },
  chalkboard: { fill: "#2E3B32", title: "#F3F1E7", body: "#E9E6D8", w: 196, font: FONT.chalk },
  postcard:   { fill: "#F6EED8", title: "#2B2418", body: "#2B2418", w: 226 },
  gifttag:    { fill: "#F6EED8", title: "#7A1A1A", body: "#2B2418", w: 196 },
  receipt:    { fill: "#FFFFFF", title: "#222222", body: "#333333", w: 150, font: FONT.typewriter, centred: true },
  starburst:  { fill: GOLD, title: NAVY, body: NAVY, w: 176, square: true, centred: true },
  pennant:    { fill: "#C9DDF7", title: "#0E2A55", body: "#0E2A55", w: 172, centred: true },
  envelope:   { fill: "#F6EED8", title: "#2B2418", body: "#2B2418", w: 204, centred: true },
  speech:     { fill: "#FFFFFF", title: NAVY, body: "#222222", w: 182 },
  heart:      { fill: "#F9C9D6", title: "#4A1426", body: "#4A1426", w: 176, centred: true },
};

// A stable lean for a note the operator didn't tilt — the app picks 2.5°–7.5° either way.
function appTilt(seed = "") {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const mag = 2.5 + (Math.abs(h) % 50) / 10;
  return h & 1 ? -mag : mag;
}

// Resolves a row's choices into what to draw. Shared by the paper and the picker swatches.
export function noteLook(note) {
  const styleKey = PAPER[note.style] ? note.style : (APP_PAPER[note.kind] || "paper");
  const base = PAPER[styleKey];
  // A polaroid's frame stays white; the colour goes into the photo instead (see NotePaper).
  const col = styleKey === "polaroid" ? null : COLOR[note.color];
  const font = FONT[note.font] || base.font || null;
  return {
    styleKey,
    appPicksStyle: !PAPER[note.style],
    fill: col ? col.paper : base.fill,
    title: col ? col.ink : base.title,
    body: col ? col.ink : base.body,
    titleFont: font ? font.title : MARKER.title,
    bodyFont: font ? font.body : (DEFAULT_BODY[styleKey] || FONT.hand).body,
    weight: font ? font.weight : 700,
    fastener: FASTENER_LABEL[note.fastener] ? note.fastener : "tape",
    appPicksFastener: !FASTENER_LABEL[note.fastener],
    tilt: note.tilt == null || note.tilt === "" ? appTilt(note.id || note.title || "") : Number(note.tilt),
    w: base.w,
    square: !!base.square,
    centred: !!base.centred,
  };
}

const isDark = (hex) => {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 120;
};

// ── the paper ─────────────────────────────────────────────────────────

export function NotePaper({ note, s = 1, showLink = false, clampBody = 0, still = false }) {
  const L = noteLook(note);
  const k = L.styleKey;
  const W = L.w * s;
  const px = (n) => `${n * s}px`;
  const icon = ICON[note.icon];
  const dark = isDark(L.fill);
  const stampInk = dark || k === "kraft" ? L.title : "#A3161B";

  // The paper itself: fill + any printed texture, and its shape.
  const paper = { position: "relative", background: L.fill, color: L.body, width: W, boxSizing: "border-box",
    padding: `${px(14)} ${px(13)} ${px(13)}`, minHeight: L.square ? W : px(104), textAlign: L.centred ? "center" : "left" };
  const extras = [];
  let iconSpot = "inline";
  let titleSize = 18;

  switch (k) {
    case "paper":
      paper.background = `linear-gradient(to right, transparent ${px(19)}, rgba(214,60,60,.45) ${px(19)} ${px(20)}, transparent ${px(20)}),
        repeating-linear-gradient(to bottom, transparent 0 ${px(15)}, rgba(70,110,200,.22) ${px(15)} ${px(16)}), ${L.fill}`;
      paper.padding = `${px(14)} ${px(11)} ${px(12)} ${px(27)}`;
      break;
    case "sticky":
      extras.push(<div key="curl" style={{ position: "absolute", right: 0, bottom: 0, width: px(24), height: px(24),
        background: "linear-gradient(135deg, transparent 50%, rgba(0,0,0,.10) 50%, rgba(255,255,255,.35))" }} />);
      break;
    case "index":
      paper.background = `linear-gradient(to bottom, transparent ${px(30)}, rgba(200,40,40,.55) ${px(30)} ${px(31.5)}, transparent ${px(31.5)}),
        repeating-linear-gradient(to bottom, transparent 0 ${px(15)}, rgba(70,110,200,.20) ${px(15)} ${px(16)}), ${L.fill}`;
      paper.paddingTop = px(9);
      break;
    case "kraft":
      paper.background = `radial-gradient(rgba(255,255,255,.10) 1px, transparent 1.6px) 0 0 / ${px(7)} ${px(7)},
        radial-gradient(rgba(0,0,0,.07) 1px, transparent 1.6px) ${px(3)} ${px(4)} / ${px(9)} ${px(9)}, ${L.fill}`;
      break;
    case "flyer":
      paper.paddingBottom = px(40);
      extras.push(<div key="tabs" style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: px(30), display: "flex", borderTop: `${Math.max(1, s)}px dashed rgba(0,0,0,.25)` }}>
        {Array.from({ length: 7 }).map((_, i) => <span key={i} style={{ flex: 1, borderLeft: i ? `${Math.max(1, s)}px dashed rgba(0,0,0,.22)` : "none" }} />)}
      </div>);
      break;
    case "coupon":
      paper.padding = px(18);
      extras.push(<div key="dash" style={{ position: "absolute", inset: px(6), border: `${Math.max(1, 2 * s)}px dashed ${L.title}`, opacity: .45, borderRadius: px(4), pointerEvents: "none" }} />);
      extras.push(<span key="sc" style={{ position: "absolute", left: px(10), top: px(-1), fontSize: px(13), lineHeight: 1, color: L.title }}>✂</span>);
      break;
    case "ticket": {
      const m = `radial-gradient(circle ${px(9)} at 0 50%, transparent 97%, #000) left / 51% 100% no-repeat,
        radial-gradient(circle ${px(9)} at 100% 50%, transparent 97%, #000) right / 51% 100% no-repeat`;
      paper.WebkitMask = m; paper.mask = m;
      paper.padding = `${px(14)} 30% ${px(14)} ${px(18)}`;
      extras.push(<div key="perf" style={{ position: "absolute", top: px(8), bottom: px(8), right: "25%", borderLeft: `${Math.max(1, 2 * s)}px dotted ${L.title}`, opacity: .45 }} />);
      iconSpot = "stub";
      break;
    }
    case "polaroid":
      paper.padding = `${px(10)} ${px(10)} ${px(12)}`;
      iconSpot = "photo";
      titleSize = 16;
      break;
    case "chalkboard":
      paper.border = `${px(7)} solid #8A5A2B`;
      paper.borderRadius = px(4);
      paper.background = `radial-gradient(rgba(255,255,255,.06) 1px, transparent 2px) 0 0 / ${px(11)} ${px(11)}, ${L.fill}`;
      break;
    case "postcard":
      paper.padding = `${px(14)} 44% ${px(14)} ${px(14)}`;
      paper.minHeight = px(130);
      extras.push(<div key="div" style={{ position: "absolute", top: px(14), bottom: px(14), left: "58%", borderLeft: `${Math.max(1, s)}px solid rgba(0,0,0,.22)` }} />);
      extras.push(<div key="stamp" style={{ position: "absolute", top: px(10), right: px(10), width: px(34), height: px(40), border: `${Math.max(1, 1.5 * s)}px dashed rgba(0,0,0,.35)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: px(18) }}>{icon || ""}</div>);
      [0, 1, 2].forEach((i) => extras.push(<div key={`l${i}`} style={{ position: "absolute", left: "63%", right: px(12), top: px(66 + i * 16), borderTop: `${Math.max(1, s)}px solid rgba(0,0,0,.2)` }} />));
      iconSpot = "none";
      break;
    case "gifttag":
      paper.clipPath = "polygon(16% 0, 100% 0, 100% 100%, 16% 100%, 0 50%)";
      paper.paddingLeft = "22%";
      extras.push(<div key="hole" style={{ position: "absolute", left: "7%", top: "50%", width: px(11), height: px(11), marginTop: px(-5.5), borderRadius: "50%", background: "rgba(0,0,0,.28)", boxShadow: "inset 0 1px 2px rgba(0,0,0,.4)" }} />);
      break;
    case "receipt": {
      const m = `linear-gradient(#000 0 0) top / 100% calc(100% - ${px(7)}) no-repeat,
        conic-gradient(from -45deg at bottom, transparent, #000 1deg 89deg, transparent 90deg) bottom / ${px(12)} ${px(7)} repeat-x`;
      paper.WebkitMask = m; paper.mask = m;
      paper.paddingBottom = px(22);
      titleSize = 15;
      break;
    }
    case "starburst": {
      const pts = [];
      for (let i = 0; i < 32; i++) {
        const r = i % 2 ? 41 : 50;
        const a = (Math.PI * 2 * i) / 32 - Math.PI / 2;
        pts.push(`${(50 + r * Math.cos(a)).toFixed(1)}% ${(50 + r * Math.sin(a)).toFixed(1)}%`);
      }
      paper.clipPath = `polygon(${pts.join(",")})`;
      paper.padding = "22% 20%";
      paper.display = "flex"; paper.flexDirection = "column"; paper.justifyContent = "center";
      titleSize = 15;
      break;
    }
    case "pennant":
      paper.clipPath = "polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%)";
      paper.paddingBottom = px(44);
      paper.minHeight = px(130);
      break;
    case "envelope":
      paper.paddingTop = px(62);
      extras.push(<div key="flap" style={{ position: "absolute", left: 0, right: 0, top: 0, height: px(56), background: "rgba(0,0,0,.07)", clipPath: "polygon(0 0, 100% 0, 50% 100%)" }} />);
      extras.push(<div key="seal" style={{ position: "absolute", left: "50%", top: px(40), width: px(24), height: px(24), marginLeft: px(-12), borderRadius: "50%", background: "#A3262B", boxShadow: "inset 0 -2px 3px rgba(0,0,0,.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: px(12) }}>{icon || ""}</div>);
      iconSpot = "none";
      break;
    case "speech":
      paper.borderRadius = px(22);
      paper.padding = `${px(14)} ${px(16)}`;
      extras.push(<div key="tail" style={{ position: "absolute", left: px(28), bottom: px(-15), width: px(24), height: px(16), background: L.fill, clipPath: "polygon(0 100%, 25% 0, 100% 0)" }} />);
      break;
    case "heart": {
      const k2 = W / 100;
      const n = (v) => (v * k2).toFixed(1);
      paper.clipPath = `path("M${n(50)} ${n(90)} C${n(18)} ${n(66)} 0 ${n(46)} 0 ${n(27)} C0 ${n(11)} ${n(12)} 0 ${n(27)} 0 C${n(38)} 0 ${n(46)} ${n(7)} ${n(50)} ${n(15)} C${n(54)} ${n(7)} ${n(62)} 0 ${n(73)} 0 C${n(88)} 0 ${n(100)} ${n(11)} ${n(100)} ${n(27)} C${n(100)} ${n(46)} ${n(82)} ${n(66)} ${n(50)} ${n(90)} Z")`;
      paper.height = W * 0.9; paper.minHeight = 0;
      paper.padding = "21% 19% 0";
      titleSize = 14;
      break;
    }
    default:
      break;
  }

  const padB = { flyer: 40, receipt: 22, pennant: 44, coupon: 18, polaroid: 12, ticket: 14, postcard: 14, speech: 14 }[k] ?? 13;
  if (note.stamp && k !== "heart" && k !== "starburst") paper.paddingBottom = px(padB + 22);

  const wrap = { position: "relative", width: W, transform: `rotate(${L.tilt}deg)`, transition: still ? "none" : "transform .25s ease",
    filter: `drop-shadow(0 ${px(3)} ${px(4)} rgba(0,0,0,.28))`, flexShrink: 0 };
  const titleStyle = { fontFamily: L.titleFont, fontWeight: L.weight, fontSize: px(titleSize), lineHeight: 1.12, color: L.title, overflowWrap: "anywhere" };
  const bodyStyle = { fontFamily: L.bodyFont, fontSize: px(k === "heart" || k === "starburst" ? 11 : 12.5), lineHeight: 1.3, color: L.body, marginTop: px(5), overflowWrap: "anywhere",
    ...(clampBody ? { display: "-webkit-box", WebkitLineClamp: clampBody, WebkitBoxOrient: "vertical", overflow: "hidden" } : null) };
  const linkText = note.link ? (SCREENS.includes(note.link) ? LINK_LABEL[note.link] : note.link.replace(/^https:\/\//, "").split("/")[0]) : null;

  return (
    <div style={wrap}>
      <div style={paper}>
        {extras}
        {iconSpot === "photo" ? (
          <div style={{ height: px(92), background: COLOR[note.color] ? COLOR[note.color].paper : "#C9DDF7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: px(38), marginBottom: px(9), boxShadow: "inset 0 0 0 1px rgba(0,0,0,.06)" }}>
            {icon || "📷"}
          </div>
        ) : null}
        {iconSpot === "stub" && icon ? (
          <span style={{ position: "absolute", right: "6%", top: "50%", transform: "translateY(-50%) rotate(-8deg)", fontSize: px(24) }}>{icon}</span>
        ) : null}
        <div style={{ position: "relative" }}>
          <div style={titleStyle}>
            {iconSpot === "inline" && icon ? <span style={{ fontSize: px(titleSize * 0.9), marginRight: px(4) }}>{icon}</span> : null}
            {note.title || <span style={{ opacity: .4 }}>Your title</span>}
          </div>
          {note.body ? <div style={bodyStyle}>{note.body}</div> : null}
          {note.action === "claim" ? (
            <span style={{ display: "inline-block", marginTop: px(8), padding: `${px(4)} ${px(10)}`, borderRadius: px(999), background: L.title, color: L.fill,
              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Rounded', system-ui, sans-serif", fontWeight: 700, fontSize: px(11) }}>
              {(note.cta || "").trim() || "Claim it"}
            </span>
          ) : null}
          {showLink && linkText ? (
            <div style={{ marginTop: px(7), fontSize: px(10.5), fontFamily: "-apple-system, system-ui, sans-serif", fontWeight: 600, color: L.body, opacity: .8 }}>Tap → {linkText}</div>
          ) : null}
        </div>
      </div>
      {/* The stamp sits on the wrapper, not the paper, so a shaped paper (heart, starburst)
          can't clip it; the paper keeps a strip clear at the bottom for it. */}
      {note.stamp ? (
        <span style={{ position: "absolute", right: px(8), bottom: px(k === "flyer" ? 36 : k === "pennant" ? 34 : 7), transform: "rotate(-11deg)", border: `${Math.max(1, 2 * s)}px solid ${stampInk}`, color: stampInk,
          borderRadius: px(4), padding: `${px(1)} ${px(5)}`, fontFamily: "'Arial Black', 'Avenir Next', system-ui, sans-serif", fontWeight: 900, fontSize: px(10.5),
          letterSpacing: ".06em", textTransform: "uppercase", opacity: .9, whiteSpace: "nowrap", background: dark ? "transparent" : "rgba(255,255,255,.18)" }}>{note.stamp}</span>
      ) : null}
      <Fastener kind={L.fastener} s={s} />
    </div>
  );
}

// What holds the paper to the glass — drawn over the top edge.
export function Fastener({ kind, s }) {
  const px = (n) => `${n * s}px`;
  const at = (style) => ({ position: "absolute", left: "50%", ...style });
  switch (kind) {
    case "tape":
      return <div style={at({ top: px(-8), width: px(54), height: px(17), marginLeft: px(-27), background: "rgba(236,226,196,.78)", transform: "rotate(-4deg)", boxShadow: "0 1px 1px rgba(0,0,0,.08)" })} />;
    case "washi":
      return <div style={at({ top: px(-8), width: px(56), height: px(16), marginLeft: px(-28), transform: "rotate(3deg)", opacity: .9,
        background: `repeating-linear-gradient(45deg, rgba(244,193,32,.85) 0 ${px(5)}, rgba(255,255,255,.85) ${px(5)} ${px(10)})` })} />;
    case "pin":
      return <div style={at({ top: px(2), width: px(14), height: px(14), marginLeft: px(-7), borderRadius: "50%", background: "radial-gradient(circle at 35% 35%, #ff8a8a, #C62D33 60%, #7d1015)", boxShadow: `0 ${px(2)} ${px(2)} rgba(0,0,0,.35)` })} />;
    case "magnet":
      return <div style={at({ top: px(-4), width: px(20), height: px(20), marginLeft: px(-10), borderRadius: "50%", background: "radial-gradient(circle at 35% 30%, #7fb0ff, #1F4FB8 65%, #12306f)", boxShadow: `0 ${px(2)} ${px(3)} rgba(0,0,0,.35)` })} />;
    case "suction":
      return <>
        <div style={at({ top: px(-12), width: px(20), height: px(20), marginLeft: px(-10), borderRadius: "50%", background: "rgba(255,255,255,.45)", border: `${Math.max(1, s)}px solid rgba(255,255,255,.85)`, boxShadow: "0 1px 2px rgba(0,0,0,.2)" })} />
        <div style={at({ top: px(6), width: px(3), height: px(9), marginLeft: px(-1.5), background: "#9aa3ad", borderRadius: px(2) })} />
      </>;
    case "clip":
      return <>
        <div style={at({ top: px(-12), width: px(22), height: px(12), marginLeft: px(-11), border: `${Math.max(1, 2 * s)}px solid #b8bec6`, borderBottom: "none", borderRadius: `${px(8)} ${px(8)} 0 0` })} />
        <div style={at({ top: px(-3), width: px(32), height: px(12), marginLeft: px(-16), background: "linear-gradient(#2b2b2b, #111)", borderRadius: px(2) })} />
      </>;
    case "paperclip":
      return <div style={{ position: "absolute", left: px(14), top: px(-10), width: px(10), height: px(30), border: `${Math.max(1, 2 * s)}px solid #9aa3ad`, borderRadius: px(6), transform: "rotate(6deg)" }} />;
    case "staple":
      return <div style={at({ top: px(6), width: px(18), height: px(3), marginLeft: px(-9), background: "#8b939c", borderRadius: px(1), boxShadow: "0 1px 0 rgba(255,255,255,.6)" })} />;
    case "bandage":
      return <div style={{ position: "absolute", left: px(-6), top: px(-2), width: px(46), height: px(14), borderRadius: px(7), transform: "rotate(-32deg)",
        background: `radial-gradient(rgba(0,0,0,.18) 0.8px, transparent 1.2px) 0 0 / ${px(4)} ${px(4)}, linear-gradient(to right, #E4B98E 0 32%, #F1D2AE 32% 68%, #E4B98E 68%)` }} />;
    case "gum":
      return <div style={at({ top: px(-5), width: px(18), height: px(13), marginLeft: px(-9), background: "#F48FB1", borderRadius: "55% 45% 50% 50% / 60% 55% 45% 40%", boxShadow: "inset 0 -2px 2px rgba(0,0,0,.12)" })} />;
    case "twine":
      return <svg style={{ position: "absolute", left: 0, top: px(-22), width: "100%", height: px(30), overflow: "visible" }} viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true">
        <polyline points="12,30 50,2 88,30" fill="none" stroke="#9C7A4F" strokeWidth={1.6} vectorEffect="non-scaling-stroke" />
        <circle cx="50" cy="2" r="2.4" fill="#6b6b6b" />
      </svg>;
    case "clothespin":
      return <>
        <div style={at({ top: px(-16), width: px(9), height: px(30), marginLeft: px(-4.5), background: "linear-gradient(to right, #C9A274, #E2C49B, #C9A274)", borderRadius: px(2) })} />
        <div style={at({ top: px(-6), width: px(11), height: px(4), marginLeft: px(-5.5), background: "#9aa3ad" })} />
      </>;
    case "sticker":
      return <div style={{ position: "absolute", right: px(-8), top: px(-8), width: px(24), height: px(24), borderRadius: "50%", background: GOLD, color: NAVY, display: "flex", alignItems: "center", justifyContent: "center", fontSize: px(13), boxShadow: "0 1px 2px rgba(0,0,0,.3)", border: `${Math.max(1, 1.5 * s)}px solid #fff` }}>★</div>;
    default:
      return null;
  }
}

// ── the Shop banner ──────────────────────────────────────────────────

const ROUNDED = `ui-rounded, "SF Pro Rounded", -apple-system, BlinkMacSystemFont, system-ui, sans-serif`;

export function bannerLook(b) {
  const style = ["hero", "gold", "glass", "tile", "ribbon", "spotlight"].includes(b.style) ? b.style : "hero";
  const col = COLOR[b.color];
  const base = {
    hero: { bg: NAVY, ink: "#FFFFFF", sub: "rgba(255,255,255,.88)", accent: GOLD },
    gold: { bg: GOLD, ink: NAVY, sub: NAVY, accent: NAVY },
    glass: { bg: "rgba(255,255,255,.62)", ink: NAVY, sub: "rgba(7,44,118,.82)", accent: NAVY },
    tile: { bg: "rgba(255,255,255,.82)", ink: NAVY, sub: "rgba(7,44,118,.82)", accent: NAVY },
    ribbon: { bg: GOLD, ink: NAVY, sub: NAVY, accent: NAVY },
    spotlight: { bg: NAVY, ink: "#FFFFFF", sub: "rgba(255,255,255,.88)", accent: GOLD },
  }[style];
  const look = col
    ? { bg: style === "glass" ? `${col.paper}C8` : col.paper, ink: col.ink, sub: col.ink, accent: isDark(col.paper) ? GOLD : NAVY }
    : base;
  const font = FONT[b.font];
  return { style, ...look, titleFont: font ? font.title : ROUNDED, bodyFont: font ? font.body : ROUNDED, weight: font ? font.weight : 800 };
}

export function BannerCard({ banner: b }) {
  const L = bannerLook(b);
  const icon = ICON[b.icon];
  const dark = isDark(L.bg.slice(0, 7));
  const chip = b.stamp ? (
    <span style={{ display: "inline-block", fontFamily: ROUNDED, fontWeight: 900, fontSize: 10.5, letterSpacing: ".08em", textTransform: "uppercase",
      padding: "4px 9px", borderRadius: 999, background: dark ? GOLD : NAVY, color: dark ? NAVY : GOLD, whiteSpace: "nowrap" }}>{b.stamp}</span>
  ) : null;
  const cta = b.action === "claim" ? (
    <span style={{ display: "inline-block", marginTop: 10, padding: "7px 14px", borderRadius: 999, fontFamily: ROUNDED, fontWeight: 800, fontSize: 13,
      background: dark ? GOLD : NAVY, color: dark ? NAVY : "#FFFFFF" }}>{(b.cta || "").trim() || "Claim it"}</span>
  ) : null;
  const chevron = b.link ? <span aria-hidden="true" style={{ fontSize: 20, fontWeight: 700, color: L.ink, opacity: .7, alignSelf: "center" }}>›</span> : null;
  const title = (size) => <div style={{ fontFamily: L.titleFont, fontWeight: L.weight, fontSize: size, lineHeight: 1.12, color: L.ink, overflowWrap: "anywhere" }}>{b.title || <span style={{ opacity: .45 }}>Your title</span>}</div>;
  const body = (size, lines) => b.body ? (
    <div style={{ fontFamily: L.bodyFont, fontWeight: 500, fontSize: size, lineHeight: 1.35, color: L.sub, marginTop: 5, overflowWrap: "anywhere",
      ...(lines ? { display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" } : null) }}>{b.body}</div>
  ) : null;

  if (L.style === "tile") {
    return (
      <div style={{ background: L.bg, borderRadius: 16, padding: 13, minHeight: 118, boxShadow: "0 0 0 1px rgba(7,44,118,.10), 0 8px 20px -12px rgba(7,44,118,.45)", position: "relative" }}>
        <div style={{ width: 32, height: 32, borderRadius: 10, background: NAVY, color: GOLD, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, marginBottom: 9 }}>{icon || "🏷️"}</div>
        {title(15)}
        {body(12.5, 3)}
        {chip ? <div style={{ position: "absolute", top: 12, right: 10 }}>{chip}</div> : null}
        {cta}
      </div>
    );
  }
  if (L.style === "ribbon") {
    return (
      <div style={{ background: L.bg, clipPath: "polygon(0 0, 100% 0, 96% 50%, 100% 100%, 0 100%, 4% 50%)", padding: "12px 26px", display: "flex", alignItems: "center", gap: 10 }}>
        {icon ? <span style={{ fontSize: 20 }}>{icon}</span> : null}
        <div style={{ flex: 1, minWidth: 0 }}>{title(16)}{body(12.5, 2)}</div>
        {chip}
        {chevron}
      </div>
    );
  }
  const glow = L.style === "spotlight" ? `radial-gradient(circle at 82% 22%, rgba(244,193,32,.48), transparent 52%), ` : "";
  return (
    <div style={{ background: `${glow}${L.bg}`, borderRadius: 18, padding: 16, position: "relative",
      ...(L.style === "glass" ? { border: "1px solid rgba(255,255,255,.85)", boxShadow: "0 0 0 1px rgba(7,44,118,.10), 0 10px 26px -14px rgba(7,44,118,.5)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)" }
        : { boxShadow: "0 10px 26px -14px rgba(7,44,118,.6)" }) }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, minHeight: chip || icon ? 30 : 0 }}>
        <div>{chip}</div>
        {icon ? <span style={{ fontSize: L.style === "spotlight" ? 36 : 28, lineHeight: 1 }}>{icon}</span> : null}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: chip || icon ? 8 : 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>{title(L.style === "hero" || L.style === "spotlight" ? 22 : 19)}{body(14)}{cta}</div>
        {chevron}
      </div>
    </div>
  );
}

// ── the mocks ────────────────────────────────────────────────────────

function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const set = () => setW(el.clientWidth);
    set();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

// The window on the customer's Home screen: a navy double-hung window, the yard soft behind the
// glass, and the live notes taped to the upper pane — the first four, in order, like the app.
export function WindowMock({ notes, label = "Preview of the window on clients' Home screen" }) {
  const [ref, w] = useWidth();
  const shown = notes.slice(0, CAPACITY.window);
  const pane = Math.max(0, w - 20);
  const n = shown.length;
  const s = n ? Math.min(0.62, (pane / n) / 182 * 0.9) : 0.6;
  return (
    <div ref={ref} role="img" aria-label={`${label}: ${n ? shown.map((x) => x.title).join(", ") : "no notes"}`}
         style={{ width: "100%", maxWidth: 440, background: "#0B2A66", borderRadius: 12, padding: 10, boxShadow: "inset 0 1px 0 rgba(255,255,255,.12), 0 10px 26px -14px rgba(7,44,118,.7)" }}>
      <div style={{ position: "relative", height: Math.max(150, pane * 0.52), borderRadius: 4, overflow: "hidden" }}>
        <div aria-hidden="true" style={{ position: "absolute", inset: -8, filter: "blur(3px)",
          background: `radial-gradient(34% 22% at 12% 84%, #557f4c 0 60%, transparent 63%), radial-gradient(30% 20% at 45% 86%, #62904f 0 58%, transparent 62%),
            radial-gradient(32% 22% at 80% 84%, #4f7a48 0 60%, transparent 63%),
            repeating-linear-gradient(to right, #EADFC8 0 9px, #D6C6A8 9px 11px) 0 100% / 100% 11% no-repeat,
            linear-gradient(180deg, #7FB6E8 0%, #BFDDF5 55%, #DCEBF8 78%, #A8CF8C 79%, #94C27A 100%)` }} />
        {shown.map((x, i) => (
          <div key={x.id || i} style={{ position: "absolute", top: `${10 + (i % 2) * 7}%`, left: `${((i + 0.5) / n) * 100}%`, transform: "translateX(-50%)" }}>
            <NotePaper note={x} s={s} clampBody={3} still />
          </div>
        ))}
        {!n ? <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: NAVY, fontSize: 13, fontWeight: 600, textShadow: "0 1px 0 rgba(255,255,255,.6)" }}>No notes up — clients see a clear window.</div> : null}
      </div>
      <div aria-hidden="true" style={{ height: 11, margin: "6px -10px", background: "#0A2558", position: "relative", boxShadow: "inset 0 1px 0 rgba(255,255,255,.1)" }}>
        <span style={{ position: "absolute", left: "50%", top: 2, width: 18, height: 7, marginLeft: -9, borderRadius: 4, background: "#C8A250" }} />
      </div>
      <div aria-hidden="true" style={{ height: Math.max(56, pane * 0.2), borderRadius: 4, filter: "saturate(.95)", background: "linear-gradient(180deg, #8DBE73, #6FA35A)", position: "relative", overflow: "hidden" }}>
        <span style={{ position: "absolute", left: "28%", bottom: 8, fontSize: 22 }}>🐕</span>
        <span style={{ position: "absolute", left: "62%", bottom: 14, fontSize: 11 }}>🎾</span>
      </div>
    </div>
  );
}

// The top of the Shop screen, in the app's light glass: live banners in order, two tiles side by
// side, then the app's own "Want something extra?" contact card with Text and Call (owner,
// 2026-11-04 — it replaced the old "coming soon" banner). The app always shows that card: under
// the banners, or alone when none are live (ShopView.swift, ShopContactCard) — so the mock does too.
export function ShopMock({ banners, orgName }) {
  const shown = banners.slice(0, CAPACITY.shop);
  const groups = [];
  shown.forEach((b) => {
    const last = groups[groups.length - 1];
    if (b.style === "tile" && last && last.tiles && last.items.length < 2) last.items.push(b);
    else groups.push({ tiles: b.style === "tile", items: [b] });
  });
  const builtIn = { style: "hero", icon: "store", title: "Want something extra?",
    body: `Contact ${(orgName || "").trim() || "us"} for more info — text or call, whichever's easier.` };
  const pill = (label, solid) => (
    <span style={{ flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 999, fontFamily: ROUNDED, fontWeight: 800, fontSize: 13,
      background: solid ? NAVY : "rgba(255,255,255,.85)", color: solid ? "#FFFFFF" : NAVY, boxShadow: solid ? "none" : "inset 0 0 0 1px rgba(7,44,118,.25)" }}>{label}</span>
  );
  return (
    <div role="img" aria-label={`Preview of the top of the Shop screen: ${shown.length ? `${shown.map((b) => b.title).join(", ")}, then the app's own contact-us card` : "the app's own contact-us card"}`}
         style={{ width: "100%", maxWidth: 360, borderRadius: 26, padding: "16px 14px 18px", border: "1px solid rgba(7,44,118,.12)",
           background: "radial-gradient(120% 60% at 20% 0%, #EAF1FB, transparent), linear-gradient(180deg, #E4ECF7, #F3F6FB)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: NAVY, marginBottom: 12 }}>
        <span aria-hidden="true" style={{ fontSize: 20, fontWeight: 600 }}>‹</span>
        <span style={{ fontFamily: ROUNDED, fontWeight: 800, fontSize: 20 }}>Shop</span>
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {groups.map((g, i) => g.tiles ? (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {g.items.map((b, j) => <BannerCard key={b.id || j} banner={b} />)}
          </div>
        ) : <BannerCard key={g.items[0].id || i} banner={g.items[0]} />)}
        <BannerCard banner={builtIn} />
        <div aria-hidden="true" style={{ display: "flex", gap: 8 }}>{pill("Text", true)}{pill("Call", false)}</div>
        <div style={{ fontSize: 11.5, color: "rgba(7,44,118,.75)", textAlign: "center" }}>
          {shown.length ? "The app's own card — it always sits under your banners." : "The app's own card — it leads while you have none live."}
        </div>
        <div aria-hidden="true" style={{ display: "grid", gap: 6, marginTop: 6, opacity: .5 }}>
          <div style={{ height: 10, width: "40%", borderRadius: 6, background: "rgba(7,44,118,.18)" }} />
          <div style={{ height: 44, borderRadius: 12, background: "rgba(255,255,255,.7)" }} />
        </div>
      </div>
    </div>
  );
}

// A design's name for screen readers and captions.
export const describeLook = (r, placement) => [
  r.style ? STYLE_LABEL[r.style] : (placement === "shop" ? "Hero" : "app's choice of paper"),
  placement === "window" ? (r.fastener ? FASTENER_LABEL[r.fastener] : "app's choice of fastener") : null,
  r.color ? COLOR[r.color]?.label : null,
  r.font ? FONT[r.font]?.label : null,
  r.stamp ? `stamped ${r.stamp}` : null,
].filter(Boolean).join(", ");
